import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Bookmark, FileText, Search, X, Copy, Check, Pencil, Star, Settings } from "lucide-react";
import { api } from "@/lib/api";
import { passtZurBelegart, BELEGART_NAME } from "@/lib/textvorlagen";
import { toast } from "sonner";
import AuswahlFeld from "@/components/AuswahlFeld";

const PLACEHOLDERS = [
  { alias: "{anrede_brief}", desc: "Sehr geehrter Herr/Frau + Name" },
  { alias: "{kunde_name}", desc: "Kundenname" },
  { alias: "{kunde_adresse}", desc: "Kundenadresse" },
  { alias: "{kunde_email}", desc: "Kunden-E-Mail" },
  { alias: "{kunde_telefon}", desc: "Kundentelefon" },
  { alias: "{firma}", desc: "Firmenname" },
  { alias: "{datum}", desc: "Heutiges Datum" },
  { alias: "{dokument_nr}", desc: "Dokument-Nr." },
  { alias: "{lohnanteil}", desc: "Lohnanteil netto" },
  { alias: "{lohnanteil_mwst}", desc: "MwSt auf Lohnanteil" },
  { alias: "{lohnanteil_brutto}", desc: "Lohnanteil brutto" },
  { alias: "{mwst_satz}", desc: "MwSt-Satz (z.B. 19,00%)" },
  { alias: "{bearbeiter}", desc: "Eingeloggter Benutzer" },
  { alias: "{zahlbar_bis}", desc: "Zahlungsziel (heute + Zahlungsfrist)" },
  { alias: "{anzahlung}", desc: "Anzahlungsbetrag" },
  { alias: "{anzahlung_offen}", desc: "Noch offene Anzahlung (AB)" },
  { alias: "{bankverbindung}", desc: "Bank, IBAN, BIC aus den Einstellungen" },
];

const getAnredeBrief = (customer) => {
  if (!customer) return "Sehr geehrte Damen und Herren";
  const anrede = customer.anrede || "";
  const fullName = customer.name || "";
  const cleanName = fullName.replace(/^(Herr|Frau|Divers)\s+/i, "").trim();
  const nameParts = cleanName.split(/\s+/);
  const nachname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : cleanName;

  if (anrede === "Herr" || fullName.startsWith("Herr ")) {
    return `Sehr geehrter Herr ${nachname}`;
  } else if (anrede === "Frau" || fullName.startsWith("Frau ")) {
    return `Sehr geehrte Frau ${nachname}`;
  } else {
    return `Sehr geehrte/r ${cleanName}`;
  }
};

const resolvePlaceholders = (text, customer, settings, docNumber, lohnanteilData, docContext) => {
  if (!text) return "";
  const now = new Date();
  const la = lohnanteilData || {};
  const ctx = docContext || {};
  const vatRate = la.vatRate || 19;
  // {bearbeiter} = eingeloggter Benutzer (Login-Name)
  let bearbeiter = "";
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    bearbeiter = u?.username || "";
  } catch { bearbeiter = ""; }
  // {zahlbar_bis} = heutiges Datum + Zahlungsfrist (due_days)
  let zahlbarBis = "";
  if (ctx.due_days != null && !isNaN(Number(ctx.due_days))) {
    const d = new Date(now);
    d.setDate(d.getDate() + Number(ctx.due_days));
    zahlbarBis = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  // {anzahlung} = Anzahlungsbetrag
  const anzahlung = (ctx.anzahlung != null && !isNaN(Number(ctx.anzahlung)))
    ? Number(ctx.anzahlung).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";
  // {anzahlung_offen} = vereinbarte minus erhaltene Anzahlung (05.10.2026)
  const anzahlungOffen = (ctx.anzahlung_offen != null && !isNaN(Number(ctx.anzahlung_offen)))
    ? Number(ctx.anzahlung_offen).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";
  const bankverbindung = [settings?.bank_name, settings?.iban && `IBAN: ${settings.iban}`, settings?.bic && `BIC: ${settings.bic}`]
    .filter(Boolean).join("\n");
  return text
    .replace(/\{anrede_brief\}/g, getAnredeBrief(customer))
    .replace(/\{kunde_name\}/g, customer?.name || "")
    .replace(/\{kunde_adresse\}/g, customer?.address || "")
    .replace(/\{kunde_email\}/g, customer?.email || "")
    .replace(/\{kunde_telefon\}/g, customer?.phone || "")
    .replace(/\{firma\}/g, settings?.company_name || "")
    .replace(/\{datum\}/g, now.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }))
    .replace(/\{dokument_nr\}/g, docNumber || "")
    .replace(/\{lohnanteil\}/g, (la.netto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{lohn_netto\}/g, (la.netto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{lohnanteil_mwst\}/g, (la.mwst || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{lohn_mwst\}/g, (la.mwst || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{lohnanteil_brutto\}/g, (la.brutto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{lohn_brutto\}/g, (la.brutto || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace(/\{mwst_satz\}/g, vatRate.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%")
    .replace(/\{bearbeiter\}/g, bearbeiter)
    .replace(/\{zahlbar_bis\}/g, zahlbarBis)
    .replace(/\{anzahlung_offen\}/g, anzahlungOffen)
    .replace(/\{bankverbindung\}/g, bankverbindung)
    .replace(/\{anzahlung\}/g, anzahlung);
};

// vorwahl (A-026): optionaler Wortstamm (z. B. "bild"); ist das Feld leer, wird die erste Vorlage vorgewählt, deren Titel oder Stichwörter ihn enthalten.
// tippName: Name der Vorlage für den Tipp, falls keine passt (nichts fest im Code, die Vorlagen pflegt Ralph selbst).
const TextTemplateSelect = ({ docType, textType, value, onChange, customer, settings, docNumber, lohnanteilData, docContext, vorwahl = "", tippName = "" }) => {
  const [templates, setTemplates] = useState([]);
  const [geladen, setGeladen] = useState(false);
  const vorgewaehlt = useRef(false);
  const [open, setOpen] = useState(false);
  const [fromTemplate, setFromTemplate] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.max(56, el.scrollHeight) + "px";
    }
  }, []);

  useEffect(() => { loadTemplates(); setShowSavePrompt(false); }, [docType, textType]);  // neuer Beleg (z. B. AB → Rechnung): alte Frage weg
  useEffect(() => { autoResize(); }, [value, autoResize]);

  const vorwahlTreffer = vorwahl
    ? templates.find((t) => [t.title, ...(t.keywords || [])].some((x) => String(x || "").toLowerCase().includes(vorwahl.toLowerCase())))
    : null;
  useEffect(() => {
    if (!vorwahl || !geladen || vorgewaehlt.current || !vorwahlTreffer) return;
    vorgewaehlt.current = true;
    if (!(value || "").trim()) handleSelect(vorwahlTreffer);   // Hand geht vor: Getipptes bleibt
    // eslint-disable-next-line
  }, [vorwahl, geladen, vorwahlTreffer]);

  const loadTemplates = async () => {
    try {
      // NUR Modul-Textvorlagen verwenden
      const modulRes = await api.get("/modules/textvorlagen/data", { params: { doc_type: docType, text_type: textType } }).catch(() => ({ data: [] }));
      setTemplates(modulRes.data || []);
      setGeladen(true);
    } catch {
      // silent
    }
  };

  const handleSelect = (template) => {
    const resolved = resolvePlaceholders(template.content, customer, settings, docNumber, lohnanteilData, docContext);
    onChange(resolved);
    setFromTemplate(true);
    setShowSavePrompt(false);
    setOpen(false);
  };

  const handleChange = (newValue) => {
    onChange(newValue);
    setFromTemplate(false);
    // Show save prompt when user types something new and it's not empty
    if (newValue.trim().length > 3) {
      const isExisting = templates.some(t => t.content === newValue || resolvePlaceholders(t.content, customer, settings, docNumber, lohnanteilData, docContext) === newValue);
      setShowSavePrompt(!isExisting);
    } else {
      setShowSavePrompt(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!value?.trim()) return;
    setSavingTemplate(true);
    try {
      // Use first ~40 chars as title
      const title = value.trim().substring(0, 40) + (value.trim().length > 40 ? "..." : "");
      await api.post("/modules/textvorlagen/data", {
        doc_type: docType,
        text_type: textType,
        title,
        content: value.trim()
      });
      toast.success("Als Textbaustein gespeichert!");
      setShowSavePrompt(false);
      setFromTemplate(true);
      loadTemplates();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingTemplate(false);
    }
  };

  const labels = { vortext: "Vortext", schlusstext: "Schlusstext", betreff: "Betreff", bemerkung: "Bemerkung", portal_nachricht: "Portal-Nachricht", email: "E-Mail-Text" };
  const label = labels[textType] || textType;
  const isBetreff = textType === "betreff";

  // BUT-0029 (29.10.2026): Auswahl wie überall in der Suite (Baustein AuswahlFeld) statt eigenem Fenster.
  // Nur Texte der Belegart plus „Allgemein“; der Standardtext steht oben und ist gekennzeichnet.
  const mitStandard = ["vortext", "schlusstext"].includes(textType) && ["angebot", "auftrag", "rechnung"].includes(docType);
  const eigene = templates.filter((t) => t.doc_type === docType && t.text_type === textType);
  const markiert = eigene.some((t) => t.standard);
  const istStandard = (t) => mitStandard && t.doc_type === docType && t.text_type === textType && (t.standard || (!markiert && eigene.length === 1));
  const rang = (t) => (istStandard(t) ? 0 : t.doc_type === docType ? 1 : 2);
  const passende = templates.filter((t) => t.title && passtZurBelegart(t, docType))
    .sort((a, b) => rang(a) - rang(b) || (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.title || "").localeCompare(b.title || ""));
  const zusatz = {};
  passende.forEach((t) => { if (!(t.title in zusatz)) { if (istStandard(t)) zusatz[t.title] = "Standard"; else if (t.doc_type === "allgemein" && docType !== "allgemein") zusatz[t.title] = "Allgemein"; } });
  const aufgeloest = (t) => resolvePlaceholders(t.content, customer, settings, docNumber, lohnanteilData, docContext);
  // Gewählt ist, was dem Text im Feld entspricht (auch beim Öffnen eines gespeicherten Belegs); Handänderung = nichts gewählt
  const gewaehlteVorlage = (value || "").trim() ? passende.find((t) => aufgeloest(t) === value || t.content === value) : null;
  const waehleNachTitel = (titel) => {
    const t = passende.find((x) => (x.title || "").trim().toLowerCase() === (titel || "").trim().toLowerCase());
    if (t) handleSelect(t);
  };
  const neuAusText = async (titel, stichwoerter) => {
    if (!(value || "").trim()) throw { response: { data: { detail: `Bitte erst den ${label} schreiben, dann „+ Neu …“ – der Text wird als Vorlage gespeichert.` } } };
    await api.post("/modules/textvorlagen/data", { doc_type: docType, text_type: textType, title: titel, content: value.trim(), keywords: stichwoerter });
    toast.success(`„${titel}“ als ${label}-Vorlage gespeichert`);
    setShowSavePrompt(false); setFromTemplate(true);
    await loadTemplates();
  };

  return (
    <div data-testid={`template-select-${textType}-${docType}`}>
      <div className="mb-1.5">
        <AuswahlFeld
          docType={docType} testId={`auswahl-${textType}-${docType}`} label={label}
          value={gewaehlteVorlage?.title || ""} onChange={waehleNachTitel}
          eintraege={passende} zusatz={zusatz} immerSuche
          onNeuAnlegen={neuAusText} stichwoerter
          leer="— Vorlage wählen oder frei schreiben —"
          verwaltenKnoten={(
            <button type="button" onClick={() => setOpen(true)} data-testid={`btn-template-open-${textType}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" title={`${label}-Vorlagen verwalten (bearbeiten, Standard festlegen, löschen)`}>
              <Settings className="w-3.5 h-3.5" /> verwalten
            </button>
          )}
        />
        {showSavePrompt && value?.trim() && (
          <div className="flex justify-end mt-1">
            <button type="button" onClick={handleSaveAsTemplate} disabled={savingTemplate} data-testid={`btn-save-as-template-${textType}`}
              className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded-sm hover:bg-amber-50 transition-colors animate-pulse">
              <Bookmark className="w-3 h-3" />
              {savingTemplate ? "..." : "Als Textbaustein speichern?"}
            </button>
          </div>
        )}
      </div>
      {isBetreff ? (
        <input
          type="text"
          data-testid={`input-${textType}`}
          value={value || ""}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => {
            if (value?.trim() && !fromTemplate) {
              const isExisting = templates.some(t => t.content === value || resolvePlaceholders(t.content, customer, settings, docNumber, lohnanteilData, docContext) === value);
              if (!isExisting) setShowSavePrompt(true);
            }
          }}
          placeholder="z.B. Angebot für Schiebetür-Reparatur"
          className="flex w-full h-10 rounded-sm border border-input bg-background px-3 py-2 text-sm font-bold ring-offset-background placeholder:text-muted-foreground placeholder:font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ color: "#003399" }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          data-testid={`input-${textType}`}
          value={value || ""}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`${label} eingeben oder aus Textbausteinen waehlen...`}
          className="flex w-full rounded-sm border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y overflow-hidden"
          style={{ minHeight: "56px" }}
        />
      )}
      {vorwahl && geladen && !vorwahlTreffer && tippName && (
        <div className="text-xs text-amber-700 dark:text-amber-300 mt-1" data-testid="vorlagen-tipp">Tipp: Vorlage „{tippName}“ unter Textvorlagen anlegen</div>
      )}
      {!isBetreff && (
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="text-[10px] text-muted-foreground bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded cursor-pointer" title="Seitenumbruch einfuegen"
            onClick={() => onChange(value ? value + "\n---\n" : "---")}>--- = Seitenumbruch (klicken zum Einfuegen)</span>
        </div>
      )}

      {/* Textvorlagen Overlay */}
      {/* an body: im Beleg liegt das Feld im verkleinerten Papier, sonst am Handy abgeschnitten (22.10.2026) */}
      {open && createPortal(<TextvorlagenOverlay
        textType={textType}
        docType={docType}
        label={label}
        templates={templates}
        customer={customer}
        settings={settings}
        docNumber={docNumber}
        lohnanteilData={lohnanteilData}
        docContext={docContext}
        onSelect={(t) => { handleSelect(t); setOpen(false); }}
        onClose={() => setOpen(false)}
      />, document.body)}
    </div>
  );
};

const DOC_TYPE_LABELS = { angebot: "Angebot", auftrag: "Auftrag", rechnung: "Rechnung", allgemein: "Allgemein", kundenportal: "Kundenportal" };
const TEXT_TYPE_LABELS = { vortext: "Vortext", schlusstext: "Schlusstext", betreff: "Betreff", bemerkung: "Bemerkung", titel: "Titel", email: "E-Mail", mahnung: "Mahnung", portal_nachricht: "Portal-Nachricht" };

const TextvorlagenOverlay = ({ textType, docType, label, templates, customer, settings, docNumber, lohnanteilData, docContext, onSelect, onClose }) => {
  const [search, setSearch] = useState("");
  const [allTemplates, setAllTemplates] = useState([]);
  const [activeFilter, setActiveFilter] = useState(textType);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", content: "", doc_type: docType || "allgemein", text_type: textType || "vortext" });
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", content: "", doc_type: "" });
  const [zeigeAndere, setZeigeAndere] = useState(false);  // Texte anderer Belegarten (Ralph 22.10.2026)
  const belegartName = BELEGART_NAME[docType] || DOC_TYPE_LABELS[docType] || docType;
  const mitStandard = ["vortext", "schlusstext"].includes(textType) && ["angebot", "auftrag", "rechnung"].includes(docType);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadAll = () => {
    api.get("/modules/textvorlagen/data").then(res => setAllTemplates(res.data || [])).catch(() => setAllTemplates(templates));
  };

  useEffect(() => { loadAll(); }, []);

  // Standard wie im Server (utils/beleg_texte.py): markiert, sonst der einzige Text dieser Belegart
  const eigene = allTemplates.filter(t => t.doc_type === docType && t.text_type === textType);
  const markiert = eigene.some(t => t.standard);
  const istStandard = t => mitStandard && t.doc_type === docType && t.text_type === textType && (t.standard || (!markiert && eigene.length === 1));
  // Beim Öffnen gleich den Standardtext auswählen und rechts zeigen (Ralph 22.10.2026)
  const vorgewaehltRef = useRef(false);
  useEffect(() => {
    if (vorgewaehltRef.current || !allTemplates.length) return;
    vorgewaehltRef.current = true;
    const std = allTemplates.find(istStandard);
    if (std) setSelectedId(std.id);
  }, [allTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nur Texte der eigenen Belegart plus „Allgemein“ (E-Mail seit 05.10., Vor-/Schlusstext/Betreff seit 22.10.2026);
  // die anderen auf Klick, damit falsch einsortierte Texte nicht verloren gehen
  const passtSuche = t => !search || t.title?.toLowerCase().includes(search.toLowerCase()) || t.content?.toLowerCase().includes(search.toLowerCase());
  const imFilter = allTemplates.filter(t => (!activeFilter || t.text_type === activeFilter) && passtSuche(t));
  const andere = imFilter.filter(t => !passtZurBelegart(t, docType));
  const filtered = imFilter.filter(t => zeigeAndere || passtZurBelegart(t, docType))
    .sort((a, b) => (passtZurBelegart(b, docType) - passtZurBelegart(a, docType)) || (istStandard(b) - istStandard(a)));

  // Stern: Standardtext dieser Belegart festlegen; Vorlage einer anderen Art wird dafür kopiert (Original bleibt)
  const alsStandard = async (t) => {
    try {
      if (t.doc_type === docType) {
        await api.put(`/modules/textvorlagen/data/${t.id}`, { ...t, standard: !t.standard });
        toast.success(t.standard ? `Kein Standard mehr für ${belegartName}` : `Standard für ${belegartName}: „${t.title}“`);
      } else {
        const art = BELEGART_NAME[t.doc_type] || DOC_TYPE_LABELS[t.doc_type] || t.doc_type;
        if (!window.confirm(`„${t.title}“ gehört zu „${art}“.\n\nAls eigene Vorlage für ${belegartName} kopieren und als Standard festlegen? Die Vorlage für „${art}“ bleibt erhalten.`)) return;
        const res = await api.post("/modules/textvorlagen/data", { title: t.title, content: t.content, doc_type: docType, text_type: t.text_type, standard: true });
        setSelectedId(res.data?.id || null);
        toast.success(`Standard für ${belegartName}: „${t.title}“`);
      }
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Speichern"); }
  };

  const textTypeCounts = {};
  allTemplates.forEach(t => { textTypeCounts[t.text_type] = (textTypeCounts[t.text_type] || 0) + 1; });

  const selectedTemplate = allTemplates.find(t => t.id === selectedId);
  const resolvedPreview = selectedTemplate ? resolvePlaceholders(selectedTemplate.content, customer, settings, docNumber, lohnanteilData, docContext) : "";

  const handleCreate = async () => {
    if (!newForm.title?.trim() || !newForm.content?.trim()) { toast.error("Titel und Inhalt erforderlich"); return; }
    setSaving(true);
    try {
      const res = await api.post("/modules/textvorlagen/data", newForm);
      toast.success("Textvorlage gespeichert und uebernommen!");
      onSelect(res.data);
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const startEdit = () => {
    if (!selectedTemplate) return;
    setEditForm({ title: selectedTemplate.title, content: selectedTemplate.content, doc_type: selectedTemplate.doc_type });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!editForm.title?.trim() || !editForm.content?.trim()) { toast.error("Titel und Inhalt erforderlich"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/modules/textvorlagen/data/${selectedId}`, {
        ...selectedTemplate, title: editForm.title, content: editForm.content, doc_type: editForm.doc_type || selectedTemplate.doc_type,
      });
      toast.success("Textvorlage aktualisiert");
      setEditMode(false);
      loadAll();
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSavingEdit(false); }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Textvorlage "${selectedTemplate.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/modules/textvorlagen/data/${selectedId}`);
      toast.success("Gelöscht");
      setSelectedId(null);
      setEditMode(false);
      loadAll();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="textvorlagen-overlay">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-4xl h-[88vh] md:h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Textvorlagen
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Vorlage auswählen für: <strong>{label}</strong>{BELEGART_NAME[docType] && docType !== "allgemein" && <> · {belegartName}</>}</p>
          </div>
          <button onClick={onClose} className="p-2 bg-muted/40 hover:bg-muted rounded-sm border border-border text-foreground" aria-label="Schließen"><X className="w-5 h-5" /></button>
        </div>

        {/* Filter-Tabs */}
        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3 pb-2 shrink-0">
          <button onClick={() => setActiveFilter("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!activeFilter ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
            Alle ({allTemplates.length})
          </button>
          {Object.entries(TEXT_TYPE_LABELS).map(([key, lbl]) => textTypeCounts[key] ? (
            <button key={key} onClick={() => setActiveFilter(activeFilter === key ? "" : key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeFilter === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {lbl} ({textTypeCounts[key]})
            </button>
          ) : null)}
        </div>

        {/* Split-Layout: Liste links, Vorschau/Formular rechts */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0 border-t">
          {/* Linke Seite: Liste (Handy: oben) */}
          <div className="w-full md:w-2/5 max-h-[45%] md:max-h-none border-b md:border-b-0 md:border-r flex flex-col">
            <div className="p-3 shrink-0 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input className="w-full h-9 pl-9 pr-3 rounded-sm border border-input bg-background text-sm" placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button type="button" onClick={() => { setShowCreate(true); setSelectedId(null); }}
                className="h-9 px-3 rounded-sm bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors shrink-0 flex items-center gap-1"
                data-testid="btn-create-template-overlay">
                + Neu
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8">Keine Vorlagen</p>
              ) : filtered.map(t => (
                <button key={t.id} type="button"
                  onClick={() => { setSelectedId(t.id); setShowCreate(false); }}
                  className={`w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors ${selectedId === t.id && !showCreate ? "bg-primary/10 border-l-2 border-l-primary" : "hover:bg-muted/50"}`}
                  data-testid={`overlay-template-${t.id}`}
                >
                  <div className="flex items-center gap-2">
                    {istStandard(t) && <Star className="w-3.5 h-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Standard" data-testid={`standard-stern-${t.id}`} />}
                    <span className="font-medium text-sm truncate text-foreground">{t.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                      t.text_type === "vortext" ? "bg-blue-100 text-blue-700" :
                      t.text_type === "schlusstext" ? "bg-green-100 text-green-700" :
                      t.text_type === "betreff" ? "bg-purple-100 text-purple-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{TEXT_TYPE_LABELS[t.text_type] || t.text_type}</span>
                    <span className={`text-[10px] shrink-0 ${passtZurBelegart(t, docType) ? "text-muted-foreground" : "text-amber-700 font-medium"}`} data-testid={`belegart-${t.id}`}>{BELEGART_NAME[t.doc_type] || DOC_TYPE_LABELS[t.doc_type] || t.doc_type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{t.content}</p>
                </button>
              ))}
              {(andere.length > 0 || zeigeAndere) && (
                <button type="button" onClick={() => setZeigeAndere(z => !z)}
                  className="w-full text-left px-4 py-2.5 text-xs text-primary hover:bg-muted/50 border-t" data-testid="btn-andere-belegarten">
                  {zeigeAndere ? `Nur Texte für ${belegartName} zeigen` : `Texte anderer Dokumentarten zeigen (${andere.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Rechte Seite: Vorschau oder Neu-Formular */}
          <div className="w-full md:w-3/5 flex-1 min-h-0 flex flex-col">
            {showCreate ? (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  <h4 className="font-semibold text-sm">Neue Textvorlage erstellen</h4>
                  <div>
                    <label className="block text-xs font-medium mb-1">Titel</label>
                    <input className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" placeholder="z.B. Standard Vortext Angebot" value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} data-testid="new-template-title" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Textart</label>
                      <select className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" value={newForm.text_type} onChange={e => setNewForm({ ...newForm, text_type: e.target.value })}>
                        {Object.entries(TEXT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Dokumenttyp</label>
                      <select className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" value={newForm.doc_type} onChange={e => setNewForm({ ...newForm, doc_type: e.target.value })}>
                        {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Inhalt</label>
                    <textarea value={newForm.content} onChange={e => setNewForm({ ...newForm, content: e.target.value })}
                      placeholder="Text eingeben..." rows={5} className="w-full rounded-sm border border-input bg-background px-3 py-2 text-sm min-h-[120px] resize-none" data-testid="new-template-content" />
                    <div className="flex flex-wrap gap-1 mt-1">
                      {PLACEHOLDERS.map(p => (
                        <button key={p.alias} type="button" onClick={() => setNewForm({ ...newForm, content: newForm.content + p.alias })}
                          className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded hover:bg-primary/10 hover:text-primary transition-colors" title={p.desc}>{p.alias}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t shrink-0 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium rounded-sm border hover:bg-muted transition-colors">Abbrechen</button>
                  <button type="button" onClick={handleCreate} disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                    data-testid="btn-save-new-template">
                    {saving ? "Speichern..." : "Speichern & Uebernehmen"}
                  </button>
                </div>
              </>
            ) : selectedTemplate ? (
              <>
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="font-semibold">{selectedTemplate.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      selectedTemplate.text_type === "vortext" ? "bg-blue-100 text-blue-700" :
                      selectedTemplate.text_type === "schlusstext" ? "bg-green-100 text-green-700" :
                      selectedTemplate.text_type === "betreff" ? "bg-purple-100 text-purple-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{TEXT_TYPE_LABELS[selectedTemplate.text_type]}</span>
                    <span className="text-xs text-muted-foreground">{BELEGART_NAME[selectedTemplate.doc_type] || DOC_TYPE_LABELS[selectedTemplate.doc_type] || selectedTemplate.doc_type}</span>
                    <div className="flex-1" />
                    {!editMode && (
                      <>
                        {mitStandard && selectedTemplate.text_type === textType && (
                          <button type="button" onClick={() => alsStandard(selectedTemplate)} data-testid="btn-als-standard"
                            className="text-xs text-amber-700 hover:text-amber-800 font-medium px-2 py-1 rounded-sm hover:bg-amber-50 inline-flex items-center gap-1"
                            title={`Wird bei neuen Belegen „${belegartName}“ automatisch eingesetzt`}>
                            <Star className={`w-3 h-3 ${istStandard(selectedTemplate) ? "fill-amber-400" : ""}`} />
                            {istStandard(selectedTemplate) ? `Standard für ${belegartName}` : `Als Standard für ${belegartName}`}
                          </button>
                        )}
                        <button type="button" onClick={startEdit} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded-sm hover:bg-blue-50 inline-flex items-center gap-1" data-testid="btn-edit-template">
                          <Pencil className="w-3 h-3" /> Bearbeiten
                        </button>
                        <button type="button" onClick={deleteTemplate} className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded-sm hover:bg-red-50 inline-flex items-center gap-1" data-testid="btn-delete-template">
                          <X className="w-3 h-3" /> Löschen
                        </button>
                      </>
                    )}
                  </div>
                  {editMode ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Titel</label>
                        <input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="edit-template-title" />
                      </div>
                      {DOC_TYPE_LABELS[editForm.doc_type] && (
                        <div>
                          <label className="block text-xs font-medium mb-1">Dokumentart</label>
                          <select value={editForm.doc_type} onChange={e => setEditForm({ ...editForm, doc_type: e.target.value })}
                            className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="edit-template-doctype">
                            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{BELEGART_NAME[k] || v}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium mb-1">Inhalt</label>
                        <textarea value={editForm.content} onChange={e => setEditForm({ ...editForm, content: e.target.value })} className="w-full rounded-sm border border-input bg-background p-3 text-sm min-h-[200px]" data-testid="edit-template-content" />
                        <p className="text-xs text-muted-foreground mt-1">Tipp: Platzhalter wie {"{{kunde}}"}, {"{{firma}}"} werden automatisch ersetzt.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-muted/30 rounded-sm p-4 border">
                      <p className="text-sm whitespace-pre-line leading-relaxed">{resolvedPreview}</p>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t shrink-0 flex justify-end gap-3">
                  {editMode ? (
                    <>
                      <button type="button" onClick={() => setEditMode(false)} className="px-4 py-2 text-sm font-medium rounded-sm border hover:bg-muted">Abbrechen</button>
                      <button type="button" onClick={saveEdit} disabled={savingEdit} className="px-4 py-2 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="btn-save-edit-template">
                        {savingEdit ? "Speichere..." : "Änderungen speichern"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-sm border hover:bg-muted transition-colors">Abbrechen</button>
                      <button type="button" onClick={() => onSelect(selectedTemplate)}
                        className="px-4 py-2 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        data-testid="btn-template-confirm">
                        <Check className="w-4 h-4 inline mr-1.5" />Uebernehmen
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Vorlage links auswaehlen</p>
                  <p className="text-xs mt-1">oder <button type="button" onClick={() => setShowCreate(true)} className="text-primary hover:underline font-medium">neue Vorlage erstellen</button></p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { TextTemplateSelect, resolvePlaceholders, PLACEHOLDERS, getAnredeBrief };
